import { Component, type ReactNode } from 'react';
import { type WithTranslation, withTranslation } from 'react-i18next';
import { Sentry } from '@/lib/sentry';
import { ErrorScreen } from './ErrorScreen';

type ErrorBoundaryState = {
  error: unknown;
  errorInfo: unknown;
};

type Props = WithTranslation & {
  children: ReactNode;
  isInContent?: boolean;
  onReload?: () => void;
  onError?: () => void;
};

class ErrorBoundary extends Component<Props, ErrorBoundaryState> {
  state = {
    error: null,
    errorInfo: null,
  };

  componentDidCatch(error: unknown, errorInfo: unknown): void {
    this.props.onError?.();
    this.setState({
      error,
      errorInfo,
    });
    Sentry.captureException(error, {
      tags: {
        runtime: true,
      },
      extra: {
        errorInfo,
      },
    });
  }

  reload(): void {
    window.location.reload();
  }

  render(): ReactNode {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <ErrorScreen
        isInContent={this.props.isInContent}
        message={this.props.t('error_boundary.message')}
        reloadLabel={this.props.t('error_boundary.reload')}
        title={this.props.t('error_boundary.title')}
        onReload={this.props.onReload || this.reload}
      />
    );
  }
}

const EnhancedErrorBoundary = withTranslation('common')(ErrorBoundary);

export { EnhancedErrorBoundary as ErrorBoundary };
